import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import HighlightPickSection2 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/HighlightPickSection2';
import HighlightPickItem2 from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/HighlightPickItem2';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import { PromotionHighlightPickModule } from '@wconcept/wconcept-common/models/renewal/promotion';
import { usePromotionTabProducts } from '@hooks/usePromotionTabProducts';
import useRenderProducts from '@hooks/useRenderProducts';
import useLinkTo from '@hooks/useLinkTo';
import useGa4Event from '@hooks/useGa4Event';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import useModalManager from '@hooks/useModalManager';
import useGlobalState from '@hooks/useGlobalState';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

const HIGHLIGHT_PICK_MAX_VISIBLE_SLOTS = 20;

const PER_SLOT_PRODUCT_COUNT = 30;
const PER_SLOT_SKELETON_COUNT = 3;

const HIGHLIGHT_PICK_PROMOTION_TAB_ITEM_STATUS_CD: Array<'01' | '02' | '04'> = [
  '01',
  '02',
  '04',
];

const HIGHLIGHT_PICK_VISIBLE_PRODUCT_STATUS_SET = new Set<string>(
  HIGHLIGHT_PICK_PROMOTION_TAB_ITEM_STATUS_CD
);

function shuffleHighlightPickModulesWithMathRandom(
  slots: PromotionHighlightPickModule[]
): PromotionHighlightPickModule[] {
  const next = [...slots];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    const swapWith = next[swapIndex];
    const currently = next[index];
    if (swapWith !== undefined && currently !== undefined) {
      next[index] = swapWith;
      next[swapIndex] = currently;
    }
  }
  return next;
}

function slotHasDisplayableHighlightPickProduct(
  module: PromotionHighlightPickModule,
  productsMap: Map<string, Product>
): boolean {
  const itemCdLimitedList = (module.itemCdList ?? []).slice(
    0,
    PER_SLOT_PRODUCT_COUNT
  );
  return itemCdLimitedList.some(itemCd => {
    const product = productsMap.get(itemCd);
    if (!product?.statusCd) return false;
    return HIGHLIGHT_PICK_VISIBLE_PRODUCT_STATUS_SET.has(product.statusCd);
  });
}

const MemoHighlightPickSection = memo(HighlightPickSection2);

type HighlightPickSlotCardProps = {
  slotBannerIndexOneBased: number;
  highlightPickSlotModule: PromotionHighlightPickModule;
  slotProducts: Product[];
  isDesktop: boolean;
  showSkeleton: boolean;
  productUnitColorType?: string;
  productSurfaceBgType?: string;
  itemColorType?: string;
  sectionTitle?: string;
  promotionId?: number;
  promotionTitle?: string;
};

const HighlightPickSlotCard = memo(
  ({
    slotBannerIndexOneBased,
    highlightPickSlotModule,
    slotProducts,
    isDesktop,
    showSkeleton,
    productUnitColorType,
    productSurfaceBgType,
    itemColorType,
    sectionTitle,
    promotionId,
    promotionTitle,
  }: HighlightPickSlotCardProps) => {
    const { linkTo } = useLinkTo();
    const { appInfo } = useGlobalState();

    const getIsGnbType = useCallback(() => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      return urlSearchParams.get('gnbType') === 'Y';
    }, []);

    const {
      sendGa4SelectItem,
      sendGa4AddToWishProduct,
      sendGa4BannerClick,
      sendGa4ButtonClick,
    } = useGa4Event();
    const { sendMolocoAddToWish } = useMolocoLogger();
    const { sendBrazeRenewalMyHeart } = useBrazeLogger();

    const { renderProducts } = useRenderProducts({
      doAfterAddMyHeart: (product, index) => {
        sendGa4AddToWishProduct({
          product,
          myheartYn: true,
          listNo: (index ?? 0) + 1,
          ecommercePath: '프로모션',
          area: 'promotion_highlight_myheart',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: slotBannerIndexOneBased,
          variable: sectionTitle,
        });
        sendMolocoAddToWish({
          itemCd: product.itemCd,
          amount: product.finalPrice,
          pageId: window.location.pathname,
        });
        sendBrazeRenewalMyHeart({
          product,
          pageId: window.location.pathname,
          useGender: true,
          defaultGender: 'women',
        });
      },
      doAfterDeleteMyHeart: (product, index) => {
        sendGa4AddToWishProduct({
          product,
          myheartYn: false,
          listNo: (index ?? 0) + 1,
          ecommercePath: '프로모션',
          area: 'promotion_highlight_myheart',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: slotBannerIndexOneBased,
          variable: sectionTitle,
        });
      },
    });

    const slotModule = highlightPickSlotModule;

    const { openRelatedPrdModal } = useModalManager({
      doAfterAddMyHeart: (product, index) => {
        sendGa4AddToWishProduct({
          product,
          myheartYn: true,
          listNo: index !== undefined ? Number(index) + 1 : undefined,
          ecommercePath: '프로모션',
          area: 'promotion_highlight_moreitem_myheart',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: slotBannerIndexOneBased,
          variable: sectionTitle,
        });
        sendMolocoAddToWish({
          itemCd: product.itemCd,
          amount: product.finalPrice,
          pageId: window.location.pathname,
        });
        sendBrazeRenewalMyHeart({
          product,
          pageId: window.location.pathname,
          useGender: true,
          defaultGender: 'women',
        });
      },
      doAfterDeleteMyHeart: (product, index) => {
        sendGa4AddToWishProduct({
          product,
          myheartYn: false,
          listNo: index !== undefined ? Number(index) + 1 : undefined,
          ecommercePath: '프로모션',
          area: 'promotion_highlight_moreitem_myheart',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: slotBannerIndexOneBased,
          variable: sectionTitle,
        });
      },
    });

    const productList: React.ReactNode = showSkeleton
      ? Array.from({ length: PER_SLOT_SKELETON_COUNT }, (_, index) => (
          <PrdItemSkeleton
            key={`highlight-pick-skeleton-${slotModule.moduleId}-${index}`}
            bgType={productSurfaceBgType}
          />
        ))
      : renderProducts({
          type: 'simple',
          products: slotProducts,
          isBrandNameKr: true,
          maxCount: PER_SLOT_PRODUCT_COUNT,
          rs: isDesktop ? '417' : '216',
          colorType: productUnitColorType,
          onClickProduct: (product, productIndex) => {
            sendGa4SelectItem({
              area: 'promotion_highlight_item',
              text: product.itemName,
              listNo: productIndex + 1,
              ecommercePath: '프로모션',
              product,
              pageNo: slotBannerIndexOneBased,
              variable: sectionTitle,
            });
          },
        });

    return (
      <HighlightPickItem2
        imgSrc={slotModule.brandImageUrlList}
        isDesktop={isDesktop}
        colorType={itemColorType}
        title={slotModule.mainText}
        desc={slotModule.subText}
        onClickBanner={
          slotModule.landingUrl
            ? () => {
                sendGa4BannerClick({
                  area: 'promotion_highlight_banner',
                  page: 'promotion',
                  issueCd: slotModule.ga4ClickEventResponse?.eventValue ?? '',
                  text: slotModule.mainText,
                  listNo: slotBannerIndexOneBased,
                  variable: sectionTitle,
                  bannerType: slotModule.ga4ClickEventResponse?.eventType,
                  bannerCd: slotModule.moduleId.toString(),
                });
                linkTo.push({ url: slotModule.landingUrl as string });
              }
            : undefined
        }
        productList={productList}
        onClickAllPrd={
          !isDesktop && slotProducts.length > 3
            ? () => {
                sendGa4ButtonClick({
                  area: 'promotion_highlight_moreitem_button',
                  page: 'promotion',
                  text: '상품 전체 보기',
                  pageNo: slotBannerIndexOneBased,
                  variable: sectionTitle,
                });
                document.body.classList.add('no-scroll');
                if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                  document.location.href = 'wconceptkr://actionBarOff';
                }
                openRelatedPrdModal({
                  placement: 'bottom',
                  className: 'is-renewal',
                  value: { type: 'highlightPick' },
                  products: slotProducts,
                  onClickProduct: (product, productIndex) => {
                    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                      document.location.href = 'wconceptkr://actionBarOn';
                    }
                    sendGa4SelectItem({
                      area: 'promotion_highlight_moreitem_item',
                      text: product.itemName,
                      listNo: productIndex + 1,
                      ecommercePath: '프로모션',
                      product,
                      pageNo: slotBannerIndexOneBased,
                      variable: sectionTitle,
                    });
                  },
                  onClose: () => {
                    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
                      document.location.href = 'wconceptkr://actionBarOn';
                    }
                    document.body.classList.remove('no-scroll');
                  },
                });
              }
            : undefined
        }
      />
    );
  }
);

HighlightPickSlotCard.displayName = 'HighlightPickSlotCard';

interface HighlightPickModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  title?: string;
  subTitle?: string;
  modules: PromotionHighlightPickModule[];
  contentId: number;
  promotionId?: number;
  promotionTitle?: string;
  isVisible?: boolean;
}

function HighlightPickModule({
  bgType,
  visualTokens,
  isDesktop,
  title,
  subTitle,
  modules,
  contentId,
  promotionId,
  promotionTitle,
  isVisible,
}: HighlightPickModuleProps) {
  const {
    presetBgType,
    customColorsLite,
    productUnitColorType,
    productSkeletonBgType: productSurfaceBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'twoField',
  });
  const sectionColorType = customColorsLite ?? presetBgType;

  const slotModules = useMemo(
    () =>
      (modules ?? []).filter(module => (module.itemCdList?.length ?? 0) > 0),
    [modules]
  );

  const slotShuffleKey = useMemo(
    () =>
      [...slotModules]
        .sort((a, b) => a.moduleId - b.moduleId)
        .map(
          m =>
            `${m.moduleId}:${m.productOrderMode}:${m.displayOrder ?? ''}:${(
              m.itemCdList ?? []
            ).join(',')}`
        )
        .join('\u001e'),
    [slotModules]
  );

  const highlightPickPlacementParts = useMemo(() => {
    const nonempty = [...slotModules].filter(
      m => (m.itemCdList?.length ?? 0) > 0
    );

    const fixedSorted = nonempty
      .filter(m => m.productOrderMode === 'FIXED')
      .sort((a, b) => {
        const orderGap =
          (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.displayOrder ?? Number.MAX_SAFE_INTEGER);
        return orderGap !== 0 ? orderGap : a.moduleId - b.moduleId;
      });

    const randomSortedByModuleIdDesc = nonempty
      .filter(m => m.productOrderMode === 'RANDOM')
      .sort((a, b) => b.moduleId - a.moduleId);

    const cappedMerged = [...fixedSorted, ...randomSortedByModuleIdDesc].slice(
      0,
      HIGHLIGHT_PICK_MAX_VISIBLE_SLOTS
    );

    const fixedBlock = cappedMerged.filter(m => m.productOrderMode === 'FIXED');
    const randomBlock = cappedMerged.filter(
      m => m.productOrderMode === 'RANDOM'
    );

    return { fixedBlock, randomBlock };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotShuffleKey]);

  const [randomBlockAfterLayoutShuffle, setRandomBlockAfterLayoutShuffle] =
    useState<PromotionHighlightPickModule[] | null>(null);
  const [shuffleAlignedSlotKey, setShuffleAlignedSlotKey] = useState<
    string | null
  >(null);

  useLayoutEffect(() => {
    const { randomBlock } = highlightPickPlacementParts;
    setRandomBlockAfterLayoutShuffle(
      shuffleHighlightPickModulesWithMathRandom(randomBlock)
    );
    setShuffleAlignedSlotKey(slotShuffleKey);
  }, [highlightPickPlacementParts, slotShuffleKey]);

  const placementMergedModules = useMemo((): PromotionHighlightPickModule[] => {
    const { fixedBlock, randomBlock } = highlightPickPlacementParts;
    const randomBlockOrdered =
      shuffleAlignedSlotKey === slotShuffleKey &&
      randomBlockAfterLayoutShuffle !== null
        ? randomBlockAfterLayoutShuffle
        : randomBlock;
    return [...fixedBlock, ...randomBlockOrdered];
  }, [
    highlightPickPlacementParts,
    randomBlockAfterLayoutShuffle,
    shuffleAlignedSlotKey,
    slotShuffleKey,
  ]);

  const allItemCdList = useMemo(
    () =>
      placementMergedModules.flatMap(m =>
        (m.itemCdList ?? []).slice(0, PER_SLOT_PRODUCT_COUNT)
      ),
    [placementMergedModules]
  );

  const { productsMap, isSuccess } = usePromotionTabProducts({
    contentId: contentId.toString(),
    tabKey: 'highlight-pick',
    itemCodes: allItemCdList,
    itemStatusCd: HIGHLIGHT_PICK_PROMOTION_TAB_ITEM_STATUS_CD,
    enabled: !!isVisible && allItemCdList.length > 0,
  });

  const slotsForCarousel = useMemo(() => {
    if (!isSuccess) return placementMergedModules;
    return placementMergedModules.filter(m =>
      slotHasDisplayableHighlightPickProduct(m, productsMap)
    );
  }, [placementMergedModules, productsMap, isSuccess]);

  const sectionChildren = useMemo(
    () =>
      slotsForCarousel.map((module, slotIndex) => {
        const orderedItemCdList = (module.itemCdList ?? []).slice(
          0,
          PER_SLOT_PRODUCT_COUNT
        );
        const slotProducts = orderedItemCdList
          .map(itemCd => productsMap.get(itemCd))
          .filter(
            (product): product is Product =>
              !!product?.statusCd &&
              HIGHLIGHT_PICK_VISIBLE_PRODUCT_STATUS_SET.has(product.statusCd)
          );

        return (
          <HighlightPickSlotCard
            key={`highlight-pick-slot-${module.moduleId}`}
            highlightPickSlotModule={module}
            slotBannerIndexOneBased={slotIndex + 1}
            slotProducts={slotProducts}
            isDesktop={isDesktop}
            showSkeleton={!isDesktop && !isSuccess}
            productUnitColorType={productUnitColorType}
            productSurfaceBgType={productSurfaceBgType}
            itemColorType={presetBgType}
            sectionTitle={title}
            promotionId={promotionId}
            promotionTitle={promotionTitle}
          />
        );
      }),
    [
      slotsForCarousel,
      productsMap,
      isDesktop,
      isSuccess,
      productUnitColorType,
      productSurfaceBgType,
      presetBgType,
      promotionId,
      promotionTitle,
      title,
    ]
  );

  if (!placementMergedModules.length) return null;

  if (isDesktop && !isSuccess) return null;

  if (!slotsForCarousel.length) return null;

  return (
    <MemoHighlightPickSection
      title={title}
      subTitle={subTitle}
      colorType={sectionColorType}
      isDesktop={isDesktop}
    >
      {sectionChildren}
    </MemoHighlightPickSection>
  );
}

export default memo(HighlightPickModule);
